import React, { useEffect, useState, useRef } from "react";
import { Upload, Trash2, Image as ImageIcon, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { MEDIA_SLOTS, useMediaCtx } from "@/context/MediaContext";
import { PrimaryButton, OutlineButton } from "@/components/ui-kit";

// In-browser image compression — keeps base64 payloads small while preserving quality.
async function compressImage(file, { maxWidth = 1800, maxHeight = 1800, quality = 0.85 } = {}) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
        const dataUrl = canvas.toDataURL(mime, quality);
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function MediaSlot({ slot, currentUrl, isCustom, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);
  const [dragging, setDragging] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErr("Please choose an image file.");
      return;
    }
    setErr(""); setBusy(true); setSuccess(false);
    try {
      const dataUrl = await compressImage(file);
      await api.put("/admin/media", { key: slot.key, data_url: dataUrl });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
      await onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Upload failed. Try a smaller image.");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ${slot.name} to default?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/media/${slot.key}`);
      await onChanged();
    } finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#1A1A1A] p-5" data-testid={`media-slot-${slot.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-lg">{slot.name}</p>
          <p className="mt-1 text-xs text-[#9CA3AF] leading-relaxed max-w-md">{slot.description}</p>
        </div>
        <span className={`px-2 py-1 rounded-md text-[10px] border tracking-wider uppercase ${isCustom ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-[#9CA3AF] border-white/15"}`}>
          {isCustom ? "Custom" : "Default"}
        </span>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`mt-4 relative rounded-lg overflow-hidden border-2 border-dashed transition aspect-[16/9] flex items-center justify-center ${dragging ? "border-[#3B82F6] bg-[#3B82F6]/5" : "border-white/10 bg-black/40"}`}
      >
        {currentUrl ? (
          <img src={currentUrl} alt={slot.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="text-center text-[#9CA3AF] flex flex-col items-center gap-2">
            <ImageIcon className="w-8 h-8" />
            <span className="text-xs">No image</span>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-sm">Uploading…</div>
        )}
        {success && (
          <div className="absolute top-2 right-2 bg-emerald-500/90 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Updated
          </div>
        )}
      </div>

      {err && <p className="mt-3 text-xs text-red-400" data-testid={`media-err-${slot.key}`}>{err}</p>}

      <div className="mt-4 flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`media-input-${slot.key}`}
        />
        <PrimaryButton onClick={() => inputRef.current?.click()} className="px-4 py-2 text-sm" data-testid={`media-upload-${slot.key}`}>
          <Upload className="w-4 h-4" /> Upload
        </PrimaryButton>
        {isCustom && (
          <OutlineButton onClick={reset} className="px-4 py-2 text-sm" data-testid={`media-reset-${slot.key}`}>
            <Trash2 className="w-4 h-4" /> Reset to default
          </OutlineButton>
        )}
        <p className="text-[10px] text-[#9CA3AF] flex items-center">Auto-resized to 1800px · JPG/PNG · max ~4 MB</p>
      </div>
    </div>
  );
}

export default function BrandMedia() {
  const { media, refresh } = useMediaCtx();
  const [adminMedia, setAdminMedia] = useState([]);

  const loadAdmin = async () => {
    const r = await api.get("/admin/media");
    setAdminMedia(r.data || []);
  };
  useEffect(() => { loadAdmin(); }, []);

  const onChanged = async () => { await Promise.all([refresh(), loadAdmin()]); };

  const lookup = (slot) => {
    const a = adminMedia.find((m) => m.key === slot.key);
    return { url: a?.data_url || slot.default_url || null, custom: !!a?.data_url };
  };

  return (
    <div>
      <h2 className="font-display text-3xl">Brand media</h2>
      <p className="mt-1 text-[#9CA3AF]">Drop in your own photography to replace the stock imagery on the site. Drag and drop, or click upload. Changes go live instantly.</p>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {MEDIA_SLOTS.map((slot) => {
          const { url, custom } = lookup(slot);
          return <MediaSlot key={slot.key} slot={slot} currentUrl={url} isCustom={custom} onChanged={onChanged} />;
        })}
      </div>
    </div>
  );
}
